import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { userAPI } from "../services/api";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

const Profile = () => {
  usePageTitle("My Profile");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Update Profile Form
  const [profileForm, setProfileForm] = useState({
    username: user?.username || "",
    email: user?.email || "",
  });

  // Change Password Form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await userAPI.updateUser(profileForm);
      if (response.data.success) {
        setMessage("Profile updated successfully!");
        setMessageType("success");
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setMessage(response.data.message);
        setMessageType("error");
      }
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to update profile");
      setMessageType("error");
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage("New passwords do not match");
      setMessageType("error");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setMessage("Password must be at least 8 characters");
      setMessageType("error");
      return;
    }

    try {
      const response = await userAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      if (response.data.success) {
        setMessage("Password changed successfully!");
        setMessageType("success");
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        setMessage(response.data.message);
        setMessageType("error");
      }
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to change password");
      setMessageType("error");
    }
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        "Are you ABSOLUTELY SURE you want to delete your account? This cannot be undone!"
      )
    ) {
      if (
        window.confirm(
          "Last chance! All your progress will be lost forever. Delete account?"
        )
      ) {
        try {
          await userAPI.deleteAccount();
          logout();
          navigate("/");
        } catch (error) {
          setMessage("Failed to delete account");
          setMessageType("error");
        }
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-white mb-8">Profile Settings</h1>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-gray-700">
        <button
          onClick={() => setActiveTab("profile")}
          className={`pb-3 px-4 font-semibold transition ${
            activeTab === "profile"
              ? "text-blue-400 border-b-2 border-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Profile
        </button>
        <button
          onClick={() => setActiveTab("password")}
          className={`pb-3 px-4 font-semibold transition ${
            activeTab === "password"
              ? "text-blue-400 border-b-2 border-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Change Password
        </button>
        <button
          onClick={() => setActiveTab("danger")}
          className={`pb-3 px-4 font-semibold transition ${
            activeTab === "danger"
              ? "text-red-400 border-b-2 border-red-400"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Danger Zone
        </button>
      </div>

      {message && (
        <div
          className={`mb-6 px-4 py-3 rounded ${
            messageType === "success"
              ? "bg-green-500/10 border border-green-500 text-green-500"
              : "bg-red-500/10 border border-red-500 text-red-500"
          }`}
        >
          {message}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === "profile" && (
        <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-white mb-4">Update Profile</h2>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2">Username</label>
              <input
                type="text"
                value={profileForm.username}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, username: e.target.value })
                }
                className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={profileForm.email}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, email: e.target.value })
                }
                className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
            >
              Update Profile
            </button>
          </form>
        </div>
      )}

      {/* Password Tab */}
      {activeTab === "password" && (
        <div className="space-y-4">
          {/* Password Security Warning */}
          <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="text-yellow-500 font-semibold text-sm mb-1">
                  Important: Keep Your Password Safe
                </h3>
                <p className="text-yellow-200/90 text-sm leading-relaxed">
                  Automatic password recovery is not yet available in this MVP
                  version. If you forget your password, email{" "}
                  <a
                    href="mailto:privacy@collectarena.com"
                    className="underline hover:text-yellow-100"
                  >
                    privacy@collectarena.com
                  </a>{" "}
                  for manual assistance. Full recovery features coming soon as
                  we grow!
                </p>
              </div>
            </div>
          </div>

          {/* Change Password Form */}
          <div className="bg-dark-card border border-gray-700 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-4">
              Change Password
            </h2>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      currentPassword: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      newPassword: e.target.value,
                    })
                  }
                  required
                  minLength="8"
                  className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      confirmPassword: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
              >
                Change Password
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Danger Zone Tab */}
      {activeTab === "danger" && (
        <div className="bg-dark-card border border-red-600 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Danger Zone</h2>
          <p className="text-gray-400 mb-6">
            Once you delete your account, there is no going back. All your
            progress will be permanently deleted.
          </p>
          <button
            onClick={handleDeleteAccount}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition"
          >
            Delete Account
          </button>
        </div>
      )}
    </div>
  );
};

export default Profile;
